<template>
  <div class="admin-root">

    <!-- ═══ 登录页 ═══ -->
    <div v-if="!loggedIn" class="login-page">
      <div class="login-bg">
        <div class="login-card">
          <div class="lc-header">
            <div class="lc-logo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-7z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
            </div>
            <h1>化工小助手</h1>
            <p>管理控制台</p>
          </div>
          <form @submit.prevent="handleLogin" class="lc-form">
            <div class="lf-field">
              <label><svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"/></svg></label>
              <input v-model="loginForm.username" type="text" placeholder="管理员账号" autocomplete="username" required />
            </div>
            <div class="lf-field">
              <label><svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/></svg></label>
              <input v-model="loginForm.password" type="password" placeholder="密码" autocomplete="current-password" required />
            </div>
            <p v-if="loginError" class="lf-err">{{ loginError }}</p>
            <button type="submit" class="lf-btn" :disabled="loginLoading">
              {{ loginLoading ? '登录中...' : '登 录' }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- ═══ 主面板 ═══ -->
    <div v-else class="panel-root">

      <!-- 侧边栏 -->
      <aside class="sidebar">
        <div class="sb-brand">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2L2 7l10 5 10-5-10-7z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
          <span>化工小助手</span>
        </div>
        <nav class="sb-nav">
          <button
            v-for="m in menuItems"
            :key="m.key"
            :class="['sb-item', { active: activeTab === m.key }]"
            @click="activeTab = m.key"
          >
            <span class="sb-icon" v-html="m.icon"></span>
            <span>{{ m.label }}</span>
            <span v-if="m.count !== undefined" class="sb-count">{{ m.count }}</span>
          </button>
        </nav>
        <div class="sb-bottom">
          <button class="sb-item sb-logout" @click="handleLogout">
            <span class="sb-icon"><svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zM11.267 8L9 10.586V7H7v6h3.586L8 15.586 9.414 17 13 13.414 16.586 17 18 15.586 14.414 12 18 8.414 16.586 7 13 10.414 7 11.267 8z" clip-rule="evenodd"/></svg></span>
            退出登录
          </button>
        </div>
      </aside>

      <!-- 右侧内容区 -->
      <div class="main-area">

        <!-- 顶部栏 -->
        <header class="topbar">
          <div class="tb-left">
            <h2>{{ currentMenuLabel }}</h2>
          </div>
          <div class="tb-right">
            <div class="tb-user">
              <div class="tb-avatar">{{ loginForm.username.slice(0,1) }}</div>
              <span class="tb-name">{{ loginForm.username }}</span>
            </div>
          </div>
        </header>

        <!-- ══ 仪表盘 ══ -->
        <main v-if="activeTab === 'dashboard'" class="content">
          <!-- 统计卡片 -->
          <div class="stat-cards">
            <div class="scard sc-revenue">
              <div class="sc-body">
                <div class="sc-label">总收入</div>
                <div class="sc-val">&yen;{{ stats.totalRevenue.toFixed(2) }}</div>
                <div class="sc-trend up">+{{ stats.todayRevenue }} 今日</div>
              </div>
              <div class="sc-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg></div>
            </div>
            <div class="scard sc-orders">
              <div class="sc-body">
                <div class="sc-label">总订单</div>
                <div class="sc-val">{{ stats.totalOrders }}</div>
                <div class="sc-trend">{{ stats.paidOrders }} 已支付</div>
              </div>
              <div class="sc-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg></div>
            </div>
            <div class="scard sc-products">
              <div class="sc-body">
                <div class="sc-label">商品数</div>
                <div class="sc-val">{{ stats.productCount }}</div>
                <div class="sc-trend">{{ stats.activeProducts }} 上架中</div>
              </div>
              <div class="sc-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg></div>
            </div>
            <div class="scard sc-download" @click="fetchSiteStats" style="cursor:pointer">
              <div class="sc-body">
                <div class="sc-label">EXE下载</div>
                <div class="sc-val">{{ siteStats.downloadCount }}</div>
                <div class="sc-trend">{{ siteStats.visitCount }} 访问</div>
              </div>
              <div class="sc-icon" style="background:#dbeafe;color:#2563eb"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></div>
            </div>
          </div>

          <!-- 最近订单 + 快捷操作 -->
          <div class="dash-grid">
            <section class="dash-card dc-wide">
              <div class="dc-head">
                <h3>最近订单</h3>
                <button class="dc-link" @click="activeTab='orders'">查看全部 &rarr;</button>
              </div>
              <div v-if="orders.length === 0" class="dc-empty">暂无订单数据</div>
              <table v-else class="mini-table">
                <thead><tr><th>订单号</th><th>商品</th><th>金额</th><th>状态</th></tr></thead>
                <tbody>
                  <tr v-for="o in recentOrders" :key="o.out_trade_no">
                    <td><code>{{ o.out_trade_no.slice(0, 18) }}...</code></td>
                    <td>{{ o.subject || '-' }}</td>
                    <td class="amt">&yen;{{ o.total_amount }}</td>
                    <td><span :class="['badge', statusBadge(o.status)]">{{ statusLabel(o.status) }}</span></td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section class="dash-card">
              <div class="dc-head"><h3>快捷操作</h3></div>
              <div class="quick-actions">
                <button class="qa-btn" @click="activeTab='products'">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 011-1h.5a1.5 1.5 0 000-3H6a1 1 0 01-1-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z"/></svg>
                  管理商品
                </button>
                <button class="qa-btn" @click="fetchOrders(); fetchProducts()">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/></svg>
                  刷新数据
                </button>
              </div>
            </section>
          </div>
        </main>

        <!-- ══ 订单管理 ══ -->
        <main v-if="activeTab === 'orders'" class="content">
          <OrdersModule />
        </main>

        <!-- ══ 商品管理 ══ -->
        <main v-if="activeTab === 'products'" class="content">
          <div class="toolbar">
            <div style="flex:1"></div>
            <button class="btn-sm btn-primary" @click="openProductEditor(null)">+ 新增商品</button>
            <button class="btn-sm btn-outline" @click="fetchProducts">
              <svg viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px"><path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/></svg>
              刷新
            </button>
          </div>

          <div v-if="productsLoading" class="state-box loading"><div class="spinner"></div>加载中...</div>
          <div v-else class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>编码</th>
                  <th>商品名称</th>
                  <th>分类</th>
                  <th>价格(元)</th>
                  <th>单位</th>
                  <th>状态</th>
                  <th>排序</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in products" :key="p.id || p.product_code">
                  <td><code>{{ p.product_code }}</code></td>
                  <td><strong>{{ p.name }}</strong></td>
                  <td><span class="cat-tag">{{ catName(p.category) }}</span></td>
                  <td>
                    <span v-if="editingPriceCode === p.product_code" class="inline-edit">
                      <input v-model.number="editingPriceVal" type="number" step="0.01" min="0" @keyup.enter="savePrice(p)" />
                      <button class="icon-btn ok" @click="savePrice(p)">&#10003;</button>
                      <button class="icon-btn cancel" @click="cancelEditPrice()">&times;</button>
                    </span>
                    <span v-else class="price-cell" @dblclick="startEditPrice(p)">
                      &yen;{{ Number(p.price).toFixed(2) }}
                      <button class="edit-pen" @click.stop="startEditPrice(p)" title="修改价格">&#9998;</button>
                    </span>
                  </td>
                  <td>{{ p.unit || '次' }}</td>
                  <td>
                    <label class="switch">
                      <input type="checkbox" :checked="!!p.is_active" @change="toggleActive(p)" />
                      <span class="slider"></span>
                    </label>
                    <span class="switch-text">{{ p.is_active ? '上架' : '下架' }}</span>
                  </td>
                  <td>{{ p.sort_order ?? '-' }}</td>
                  <td>
                    <button class="btn-link" @click="openProductEditor(p)">编辑</button>
                    <button class="btn-link danger" @click="confirmDelete(p)" v-if="!p.is_active">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </main>

        <!-- ══ 系统公告管理 ══ -->
        <main v-if="activeTab === 'announcement'" class="content">
          <AnnouncementModule />
        </main>

      </div><!-- /main-area -->
    </div><!-- /panel-root -->

    <!-- 订单详情弹窗 -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showOrderDetail" class="modal-overlay" @click.self="showOrderDetail = false">
          <div class="modal-box">
            <div class="modal-head">
              <h3>订单详情</h3>
              <button class="modal-x" @click="showOrderDetail = false">&times;</button>
            </div>
            <div class="modal-content" v-if="detailOrder">
              <div class="detail-grid">
                <div class="dg-row">
                  <span class="dg-k">商户订单号</span>
                  <span class="dg-v mono"><code>{{ detailOrder.out_trade_no }}</code></span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">支付宝交易号</span>
                  <span class="dg-v">{{ detailOrder.trade_no || '-' }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">商品名称</span>
                  <span class="dg-v strong">{{ detailOrder.subject }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">金额</span>
                  <span class="dg-v amount-lg">&yen;{{ detailOrder.total_amount }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">支付状态</span>
                  <span class="dg-v"><span :class="['badge', statusBadge(detailOrder.status)]">{{ statusLabel(detailOrder.status) }}</span></span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">执行状态</span>
                  <span class="dg-v"><span :class="['badge exec-badge', execBadge(detailOrder.exec_status)]">{{ execLabel(detailOrder.exec_status) }}</span></span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">客户端机器ID</span>
                  <span class="dg-v mono">{{ detailOrder.client_machine_id || '-' }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">客户端版本</span>
                  <span class="dg-v">{{ detailOrder.client_version || '-' }}</span>
                </div>
                <div class="dg-row dg-full">
                  <span class="dg-k">购买项目</span>
                  <pre class="dg-pre">{{ formatJson(detailOrder.items) }}</pre>
                </div>
                <div class="dg-row">
                  <span class="dg-k">创建时间</span>
                  <span class="dg-v">{{ formatTime(detailOrder.gmt_create || detailOrder.created_at) }}</span>
                </div>
                <div class="dg-row">
                  <span class="dg-k">支付时间</span>
                  <span class="dg-v">{{ formatTime(detailOrder.gmt_payment || detailOrder.paid_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 商品编辑弹窗 -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showProductEditor" class="modal-overlay" @click.self="showProductEditor = false">
          <div class="modal-box modal-md">
            <div class="modal-head">
              <h3>{{ editingProduct?.id ? '编辑商品' : '新增商品' }}</h3>
              <button class="modal-x" @click="showProductEditor = false">&times;</button>
            </div>
            <div class="modal-content">
              <form @submit.prevent="saveProduct" class="ef-form">
                <div class="ef-field"><label>商品编码 *</label><input v-model="editForm.product_code" required placeholder="如 full_flow_material" /></div>
                <div class="ef-field"><label>商品名称 *</label><input v-model="editForm.name" required /></div>
                <div class="ef-field"><label>描述</label><textarea v-model="editForm.description" rows="2" placeholder="简短描述..."></textarea></div>
                <div class="ef-row">
                  <div class="ef-field"><label>分类</label>
                    <select v-model="editForm.category">
                      <option value="full_flow">全流程</option>
                      <option value="section">工段</option>
                      <option value="single">单模块</option>
                    </select>
                  </div>
                  <div class="ef-field"><label>价格(元) *</label><input v-model.number="editForm.price" type="number" step="0.01" min="0" required /></div>
                </div>
                <div class="ef-row">
                  <div class="ef-field"><label>单位</label><input v-model="editForm.unit" placeholder="次/项/每个工段" /></div>
                  <div class="ef-field"><label>排序</label><input v-model.number="editForm.sort_order" type="number" min="0" /></div>
                </div>
                <div class="ef-check">
                  <label class="checkbox-label"><input type="checkbox" v-model="editForm.is_active" /><span>立即上架</span></label>
                </div>
                <div class="ef-actions">
                  <button type="submit" class="btn-sm btn-primary" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button>
                  <button type="button" class="btn-sm btn-outline" @click="showProductEditor = false">取消</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Toast -->
    <Transition name="toast-anim">
      <div v-if="toastMsg" :class="['toast', toastType]">{{ toastMsg }}</div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import AnnouncementModule from './admin/modules/announcement/AnnouncementModule.vue'
import OrdersModule from './admin/modules/orders/OrdersModule.vue'

/* ── State ── */
const API_BASE = ''

// Auth
const loggedIn = ref(false)
const loginLoading = ref(false)
const loginError = ref('')
const loginForm = reactive({ username: '', password: '' })

// Nav
const activeTab = ref('dashboard')

// Site Stats (访问统计)
const siteStats = ref({
  visitCount: 0,
  downloadCount: 0
})

async function fetchSiteStats() {
  try {
    const res = await fetch(`${API_BASE}/api/stats`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    if (data.success) {
      siteStats.value = {
        visitCount: data.visitCount || 0,
        downloadCount: data.downloadCount || 0
      }
    }
  } catch (e) {
    console.error('[Admin] Fetch site stats error:', e)
  }
}

// Orders
const orders = ref([])
const ordersLoading = ref(false)
const orderSearch = ref('')
const orderStatusFilter = ref('')
const execFilter = ref('')
const orderPage = ref(1)
const pageSize = 20
const totalOrders = ref(0)

// Products
const products = ref([])
const productsLoading = ref(false)

// Editing
const editingPriceCode = ref('')
const editingPriceVal = ref(0)
const showOrderDetail = ref(false)
const detailOrder = ref(null)
const showProductEditor = ref(false)
const editingProduct = ref(null)
const saving = ref(false)
const editForm = reactive({
  product_code: '', name: '', description: '', category: 'full_flow',
  price: 0, unit: '次', sort_order: 0, is_active: true,
})

// Toast
const toastMsg = ref('')
const toastType = ref('info')
let toastTimer = null



/* ── Computed: Menu ── */
const menuItems = computed(() => [
  {
    key: 'dashboard', label: '仪表盘',
    icon: '<svg viewBox="0 0 20 20" fill="currentColor"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/></svg>',
  },
  {
    key: 'orders', label: '订单管理',
    count: orders.value.length,
    icon: '<svg viewBox="0 0 20 20" fill="currentColor"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd"/></svg>',
  },
  {
    key: 'products', label: '商品管理',
    icon: '<svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 011-1h.5a1.5 1.5 0 000-3H6a1 1 0 01-1-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z"/></svg>',
  },
  {
    key: 'announcement', label: '系统公告',
    icon: '<svg viewBox="0 0 20 20" fill="currentColor"><path d="M18 3a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2h4l3 3 3-3h4a2 2 0 002-2V3zm-5 8H7V9h6v2zm0-3H7V6h6v2z"/></svg>',
  },
])

const currentMenuLabel = computed(() => {
  const m = menuItems.value.find(i => i.key === activeTab.value)
  return m ? m.label : ''
})

/* ── Computed: Stats ── */
const stats = computed(() => {
  const paid = orders.value.filter(o => o.status === 'paid')
  const totalRev = paid.reduce((s, o) => s + Number(o.total_amount || 0), 0)
  const todayStr = new Date().toISOString().slice(0, 10)
  const todayRev = paid.filter(o => (o.gmt_create || o.created_at || '').startsWith(todayStr)).reduce((s, o) => s + Number(o.total_amount || 0), 0)
  return {
    totalRevenue: totalRev,
    todayRevenue: todayRev.toFixed(2),
    totalOrders: orders.value.length,
    paidOrders: paid.length,
    productCount: products.value.length,
    activeProducts: products.value.filter(p => p.is_active).length,
    pendingExec: orders.value.filter(o => o.exec_status === 'pending' && o.status === 'paid').length,
  }
})

const recentOrders = computed(() => orders.value.slice(0, 5))

/* ── Computed: Filtered Orders ── */
const filteredOrders = computed(() => {
  let list = orders.value
  if (orderStatusFilter.value) list = list.filter(o => o.status === orderStatusFilter.value)
  if (execFilter.value) list = list.filter(o => o.exec_status === execFilter.value)
  if (orderSearch.value.trim()) {
    const q = orderSearch.value.trim().toLowerCase()
    list = list.filter(o =>
      (o.out_trade_no && o.out_trade_no.toLowerCase().includes(q)) ||
      (o.subject && o.subject.toLowerCase().includes(q))
    )
  }
  return list
})

/* ── Helpers ── */
function showToast(msg, type = 'info') {
  toastMsg.value = msg; toastType.value = type
  clearTimeout(toastTimer); toastTimer = setTimeout(() => { toastMsg.value = '' }, 2500)
}

function statusLabel(s) {
  return { unpaid: '未支付', paid: '已支付', closed: '已关闭', refunded: '已退款', expired: '已过期' }[s] || s || '-'
}
function statusBadge(s) {
  return { paid: 'ok', unpaid: 'warn', closed: 'muted', refunded: 'err', expired: 'muted' }[s] || 'muted'
}
function execLabel(s) {
  return { pending: '待执行', running: '运行中', success: '成功', failed: '失败' }[s] || s || '-'
}
function execBadge(s) {
  return { success: 'ok', pending: 'warn', running: 'running', failed: 'err' }[s] || 'muted'
}
function catName(c) {
  return { full_flow: '全流程', section: '工段', single: '单模块' }[c] || c
}

function formatTime(t) {
  if (!t) return '-'
  try { return new Date(t).toLocaleString('zh-CN') } catch { return t }
}
function formatJson(v) {
  if (!v) return '-'
  try { return typeof v === 'string' ? JSON.stringify(JSON.parse(v), null, 2) : JSON.stringify(v, null, 2) } catch { return v }
}

/* ── Auth ── */
async function handleLogin() {
  loginError.value = ''; loginLoading.value = true
  try {
    const res = await fetch(`${API_BASE}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: loginForm.username, password: loginForm.password }),
    })
    const data = await res.json()
    if (data.success) {
      localStorage.setItem('admin_token', data.token)
      localStorage.setItem('admin_user', loginForm.username)
      loggedIn.value = true
      showToast('登录成功，欢迎回来', 'success')
      fetchOrders(); fetchProducts(); fetchSiteStats()
    } else {
      loginError.value = data.message || '登录失败'
    }
  } catch (e) {
    loginError.value = '网络错误：' + e.message
  } finally {
    loginLoading.value = false
  }
}

function handleLogout() {
  localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user')
  loggedIn.value = false; loginForm.username = ''; activeTab.value = 'dashboard'
}

function checkAuth() {
  const token = localStorage.getItem('admin_token')
  if (token) {
    loginForm.username = localStorage.getItem('admin_user') || ''
    loggedIn.value = true
    fetchOrders(); fetchProducts(); fetchSiteStats()
  }
}

/* ── Orders ── */
async function fetchOrders() {
  ordersLoading.value = true
  try {
    const params = new URLSearchParams({ page: orderPage.value, size: pageSize })
    if (orderStatusFilter.value) params.set('status', orderStatusFilter.value)
    const res = await fetch(`${API_BASE}/api/admin/orders?${params}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
    })
    const data = await res.json()
    if (data.success) {
      orders.value = data.orders || []
      totalOrders.value = data.total || orders.value.length
    } else if (data.code === 'UNAUTHORIZED') {
      handleLogout(); showToast('登录已过期，请重新登录', 'warn')
    } else {
      console.error('[Admin] Fetch orders error:', data)
      showToast(data.message || '获取订单失败', 'error')
    }
  } catch (e) {
    console.error('[Admin] Fetch orders exception:', e)
    showToast('网络错误：' + e.message, 'error')
  } finally {
    ordersLoading.value = false
  }
}

function viewOrderDetail(o) { detailOrder.value = o; showOrderDetail.value = true }

/* ── Products ── */
async function fetchProducts() {
  productsLoading.value = true
  try {
    const res = await fetch(`${API_BASE}/api/admin/products`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
    })
    const data = await res.json()
    if (data.success) { products.value = data.products || [] }
  } catch (e) {
    showToast('获取商品失败: ' + e.message, 'error')
  } finally {
    productsLoading.value = false
  }
}

function startEditPrice(p) {
  editingPriceCode.value = p.product_code
  editingPriceVal.value = Number(p.price)
}
async function savePrice(p) {
  try {
    const res = await fetch(`${API_BASE}/api/admin/products/${p.id || p.product_code}/price`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
      body: JSON.stringify({ price: editingPriceVal.value }),
    })
    const data = await res.json()
    if (data.success) { p.price = editingPriceVal.value; cancelEditPrice(); showToast('价格已更新', 'success') }
    else { showToast(data.message || '更新失败', 'error') }
  } catch (e) { showToast(e.message, 'error') }
}
function cancelEditPrice() { editingPriceCode.value = ''; editingPriceVal.value = 0 }

async function toggleActive(p) {
  try {
    const res = await fetch(`${API_BASE}/api/admin/products/${p.id || p.product_code}/toggle`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
    })
    const data = await res.json()
    if (data.success) {
      p.is_active = !p.is_active
      showToast(p.is_active ? '已上架' : '已下架', 'success')
    }
  } catch (e) { showToast(e.message, 'error') }
}

function openProductEditor(p) {
  if (p) {
    editingProduct.value = p
    Object.assign(editForm, { ...p })
  } else {
    editingProduct.value = null
    Object.assign(editForm, { product_code: '', name: '', description: '', category: 'full_flow', price: 0, unit: '次', sort_order: 0, is_active: true })
  }
  showProductEditor.value = true
}

async function saveProduct() {
  saving.value = true
  try {
    const method = editingProduct.value?.id ? 'PUT' : 'POST'
    let url = `${API_BASE}/api/admin/products`
    if (editingProduct.value?.id) url += `/${editingProduct.value.id}`
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
      body: JSON.stringify(editForm),
    })
    const data = await res.json()
    if (data.success) {
      showProductEditor.value = false
      showToast(editingProduct.value ? '已更新' : '已创建', 'success')
      fetchProducts()
    } else { showToast(data.message || '操作失败', 'error') }
  } catch (e) { showToast(e.message, 'error') }
  finally { saving.value = false }
}

async function confirmDelete(p) {
  if (!confirm(`确认删除商品「${p.name}」？此操作不可撤销。`)) return
  try {
    const res = await fetch(`${API_BASE}/api/admin/products/${p.id || p.product_code}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` },
    })
    const data = await res.json()
    if (data.success) { showToast('已删除', 'success'); fetchProducts() }
    else { showToast(data.message || '删除失败', 'error') }
  } catch (e) { showToast(e.message, 'error') }
}

onMounted(() => {
  checkAuth()
})
</script>

<style scoped>
/* ═════════════════════════════════════════
   Design Tokens
   ═════════════════════════════════════════ */
.admin-root {
  --primary: #4f46e5;
  --primary-hover: #4338ca;
  --primary-light: #eef2ff;
  --primary-soft: rgba(79,70,229,0.08);
  --success: #059669;
  --success-bg: #ecfdf5;
  --warning: #d97706;
  --warning-bg: #fffbeb;
  --danger: #dc2626;
  --danger-bg: #fef2f2;
  --info: #6b7280;
  --info-bg: #f9fafb;

  --bg: #f3f4f6;
  --surface: #ffffff;
  --border: #e5e7eb;
  --border-light: #f3f4f6;
  --text: #111827;
  --text-2: #374151;
  --text-3: #9ca3af;
  --text-4: #d1d5db;

  font-family: -apple-system,BlinkMacSystemFont,'Segoe UI','PingFang SC','Microsoft YaHei',sans-serif;
  color: var(--text);
  background: var(--bg);
  height: 100vh;
  display: flex;
  overflow: hidden;
}

/* ═══ Login Page ═══ */
.login-page {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
}
.login-bg {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%;
  padding: 40px;
}
.login-card {
  width: 400px; background: var(--surface);
  border-radius: 20px; overflow: hidden;
  box-shadow: 0 25px 80px rgba(0,0,0,.25), 0 0 0 1px rgba(255,255,255,.1);
}
.lc-header {
  text-align: center;
  padding: 40px 32px 28px;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff;
}
.lc-logo { width: 48px; height: 48px; margin: 0 auto 14px; color: rgba(255,255,255,.85); }
.lc-logo svg { width: 100%; height: 100%; filter: drop-shadow(0 2px 8px rgba(0,0,0,.15)); }
.lc-header h1 { font-size: 22px; font-weight: 800; margin: 0 0 4px; letter-spacing: -.02em; }
.lc-header p { font-size: 13px; opacity: .75; margin: 0; }
.lc-form { padding: 32px; display: flex; flex-direction: column; gap: 4px; }
.lf-field {
  display: flex; align-items: center; gap: 10px;
  background: var(--bg); border: 1.5px solid transparent;
  border-radius: 10px; padding: 0 14px; margin-top: 14px;
  transition: border-color .2s;
}
.lf-field:focus-within { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-soft); }
.lf-field label { color: var(--text-3); display: flex; align-items: center; }
.lf-field label svg { width: 18px; height: 18px; }
.lf-field input {
  flex: 1; border: none; background: none; padding: 12px 0;
  font-size: 14px; color: var(--text); outline: none; font-family: inherit;
}
.lf-err { color: var(--danger); font-size: 12.5px; margin: 6px 0 2px; text-align: left; }
.lf-btn {
  margin-top: 18px; padding: 12px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, var(--primary), #7c3aed);
  color: #fff; font-size: 15px; font-weight: 700;
  cursor: pointer; transition: transform .15s, box-shadow .2s; font-family: inherit;
  letter-spacing: .02em;
}
.lf-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 25px rgba(79,70,229,.35); }
.lf-btn:disabled { opacity: .6; cursor: not-allowed; }

/* ═══ Panel Layout ═══ */
.panel-root { width: 100%; height: 100vh; display: flex; }

/* ═══ Sidebar ═══ */
.sidebar {
  width: 240px; flex-shrink: 0;
  background: var(--surface);
  border-right: 1px solid var(--border);
  display: flex; flex-direction: column;
  z-index: 10;
}
.sb-brand {
  display: flex; align-items: center; gap: 10px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--border-light);
  font-size: 15px; font-weight: 700; color: var(--text);
}
.sb-brand svg { width: 26px; height: 26px; color: var(--primary); flex-shrink: 0; }
.sb-nav { flex: 1; padding: 12px 8px; display: flex; flex-direction: column; gap: 2px; }
.sb-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; border-radius: 10px;
  border: none; background: transparent;
  cursor: pointer; font-size: 14px; font-family: inherit;
  color: var(--text-2); transition: all .15s;
}
.sb-item svg, .sb-item :deep(svg) { width: 20px; height: 20px; flex-shrink: 0; opacity: .65; }
.sb-item:hover:not(.sb-logout):not(.active) { background: var(--bg); color: var(--text-2); }
.sb-item.active {
  background: var(--primary-light); color: var(--primary); font-weight: 600;
}
.sb-item.active svg, .sb-item.active :deep(svg) { opacity: 1; color: var(--primary); }
.sb-count {
  margin-left: auto; font-size: 11px; font-weight: 600;
  padding: 1px 8px; border-radius: 10px;
  background: var(--primary-soft); color: var(--primary);
}
.sb-bottom { padding: 8px; border-top: 1px solid var(--border-light); }
.sb-logout { color: var(--text-3) !important; font-size: 13px !important; }
.sb-logout:hover { color: var(--danger) !important; background: var(--danger-bg) !important; }

/* ═══ Main Area ═══ */
.main-area { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }

.topbar {
  height: 60px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 28px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.tb-left h2 { font-size: 18px; font-weight: 700; margin: 0; }
.tb-right { display: flex; align-items: center; }
.tb-user { display: flex; align-items: center; gap: 8px; }
.tb-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), #7c3aed);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 700;
}
.tb-name { font-size: 13px; color: var(--text-2); font-weight: 500; }

.content {
  flex: 1; overflow-y: auto;
  padding: 24px 28px;
}

/* ═══ Stat Cards (Dashboard) ═══ */
.stat-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}
.scard {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 22px 24px;
  display: flex; align-items: flex-start; justify-content: space-between;
  transition: box-shadow .2s, transform .15s;
}
.scard:hover { box-shadow: 0 8px 30px rgba(0,0,0,.06); transform: translateY(-1px); }
.sc-body { display: flex; flex-direction: column; gap: 4px; }
.sc-label { font-size: 12.5px; color: var(--text-3); font-weight: 500; letter-spacing: .02em; text-transform: uppercase; }
.sc-val { font-size: 28px; font-weight: 800; line-height: 1.2; color: var(--text); letter-spacing: -.02em; }
.sc-trend { font-size: 12.5px; color: var(--success); font-weight: 600; margin-top: 4px; }
.sc-trend.warn { color: var(--warning); }
.sc-trend.up::before { content: ''; }
.sc-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sc-icon svg { width: 22px; height: 22px; }
.sc-revenue .sc-icon { background: #ecfdf5; color: var(--success); }
.sc-orders .sc-icon { background: var(--primary-light); color: var(--primary); }
.sc-products .sc-icon { background: #fef3c7; color: #d97706; }
.sc-pending .sc-icon { background: var(--danger-bg); color: var(--danger); }

/* ═══ Dashboard Grid ═══ */
.dash-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; }
.dash-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 20px 24px;
}
.dc-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.dc-head h3 { font-size: 15px; font-weight: 700; margin: 0; }
.dc-link { font-size: 13px; color: var(--primary); background: none; border: none; cursor: pointer; font-family: inherit; font-weight: 600; }
.dc-link:hover { text-decoration: underline; }
.dc-empty { text-align: center; padding: 40px 0; color: var(--text-3); font-size: 14px; }

.mini-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.mini-table th { text-align: left; padding: 8px 10px; font-size: 11.5px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid var(--border); }
.mini-table td { padding: 10px; border-bottom: 1px solid var(--border-light); vertical-align: middle; }
.mini-table tr:last-child td { border-bottom: none; }
.amt { font-weight: 700; color: var(--success); }

.quick-actions { display: flex; flex-direction: column; gap: 10px; }
.qa-btn {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; border-radius: 10px; border: 1px solid var(--border);
  background: var(--bg); color: var(--text-2); font-size: 13.5px; font-weight: 500;
  cursor: pointer; transition: all .15s; font-family: inherit;
  text-align: left;
}
.qa-btn:hover { border-color: var(--primary); color: var(--primary); background: var(--primary-light); }
.qa-btn svg { width: 18px; height: 18px; flex-shrink: 0; opacity: .6; }

/* ═══ Toolbar ═══ */
.toolbar {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 20px; flex-wrap: wrap;
}
.search-box {
  display: flex; align-items: center; gap: 8px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 10px; padding: 0 12px;
  min-width: 260px;
  transition: border-color .2s;
}
.search-box:focus-within { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-soft); }
.search-box svg { width: 17px; height: 17px; color: var(--text-3); flex-shrink: 0; }
.search-box input {
  border: none; background: none; padding: 10px 0;
  font-size: 13.5px; color: var(--text); outline: none; font-family: inherit; width: 100%;
}
.filter-group { display: flex; gap: 8px; }
.filter-group select {
  padding: 9px 14px; border-radius: 10px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-2); font-size: 13px;
  font-family: inherit; cursor: pointer; outline: none;
}
.filter-group select:focus { border-color: var(--primary); }

/* ═══ Buttons ═══ */
.btn-sm {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .15s; font-family: inherit;
  border: 1px solid transparent;
}
.btn-primary { background: var(--primary); color: #fff; }
.btn-primary:hover:not(:disabled) { background: var(--primary-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(79,70,229,.25); }
.btn-outline { background: var(--surface); color: var(--text-2); border-color: var(--border); }
.btn-outline:hover { border-color: var(--primary); color: var(--primary); }

/* ═══ Table ═══ */
.table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.data-table th {
  text-align: left; padding: 12px 16px; font-size: 11.5px; font-weight: 600;
  color: var(--text-3); text-transform: uppercase; letter-spacing: .04em;
  background: var(--bg); border-bottom: 1px solid var(--border); white-space: nowrap;
}
.data-table td {
  padding: 12px 16px; border-bottom: 1px solid var(--border-light); vertical-align: middle; font-size: 13px;
}
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover td { background: var(--primary-soft); }
.code, code { font-family: 'Cascadia Code','Fira Code','Consolas',monospace; font-size: 12px; background: var(--bg); padding: 2px 7px; border-radius: 5px; white-space: nowrap; max-width: 200px; overflow: hidden; text-overflow: ellipsis; display: inline-block; }
.mono { font-family: 'Cascadia Code','Consolas',monospace; font-size: 12px; }
.amount { font-weight: 700; color: var(--success) !important; }
.time-col { color: var(--text-3); font-size: 12px; white-space: nowrap; }

/* Badges */
.badge {
  display: inline-flex; align-items: center; font-size: 11.5px; font-weight: 600;
  padding: 3px 10px; border-radius: 6px; letter-spacing: .02em;
}
.badge.ok { background: var(--success-bg); color: var(--success); }
.badge.warn { background: var(--warning-bg); color: var(--warning); }
.badge.err { background: var(--danger-bg); color: var(--danger); }
.badge.muted { background: var(--info-bg); color: var(--info); }
.exec-badge.running { background: var(--primary-light); color: var(--primary); }

.cat-tag { font-size: 11.5px; padding: 2px 10px; border-radius: 5px; background: var(--primary-soft); color: var(--primary); font-weight: 600; }

/* Price edit */
.inline-edit { display: flex; align-items: center; gap: 4px; }
.inline-edit input {
  width: 80px; padding: 4px 8px; border-radius: 5px; border: 1.5px solid var(--primary);
  background: var(--surface); color: var(--success); font-size: 13px; font-weight: 700; font-family: inherit;
  outline: none;
}
.icon-btn { background: none; border: none; cursor: pointer; font-size: 15px; padding: 2px 5px; }
.icon-btn.ok { color: var(--success); }
.icon-btn.cancel { color: var(--danger); }
.price-cell { cursor: default; }
.edit-pen { background: none; border: none; color: var(--text-4); cursor: pointer; font-size: 14px; opacity: 0; margin-left: 4px; vertical-align: middle; transition: opacity .15s; }
.price-cell:hover .edit-pen { opacity: 1; }

/* Switch toggle */
.switch { position: relative; display: inline-block; width: 38px; height: 21px; vertical-align: middle; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider {
  position: absolute; inset: 0; cursor: pointer; border-radius: 21px;
  background: var(--border); transition: .2s;
}
.slider::before {
  content: ''; position: absolute; height: 17px; width: 17px; left: 2px; bottom: 2px;
  border-radius: 50%; background: #fff; transition: .2s; box-shadow: 0 1px 3px rgba(0,0,0,.15);
}
.switch input:checked + .slider { background: var(--primary); }
.switch input:checked + .slider::before { transform: translateX(17px); }
.switch-text { font-size: 12px; color: var(--text-3); margin-left: 6px; }

.btn-link { background: none; border: none; color: var(--primary); cursor: pointer; font-size: 12.5px; font-family: inherit; font-weight: 600; padding: 2px 6px; }
.btn-link:hover { text-decoration: underline; }
.btn-link.danger { color: var(--danger); }

/* Pagination */
.pagination {
  display: flex; align-items: center; justify-content: center; gap: 16px;
  padding: 16px; border-top: 1px solid var(--border-light);
}
.pg-info { font-size: 13px; color: var(--text-2); }
.pg-info b { color: var(--text); }
.pagination button {
  width: 32px; height: 32px; border-radius: 8px;
  background: var(--surface); border: 1px solid var(--border);
  color: var(--text-2); cursor: pointer; font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  font-family: inherit;
}
.pagination button:disabled { opacity: .3; cursor: default; }
.pagination button:not(:disabled):hover { border-color: var(--primary); color: var(--primary); }

/* State boxes */
.state-box { text-align: center; padding: 60px 0; color: var(--text-3); font-size: 14px; background: var(--surface); border: 1px solid var(--border); border-radius: 14px; }
.state-box.loading { display: flex; align-items: center; justify-content: center; gap: 12px; }
.state-box.empty svg { width: 48px; height: 48px; margin-bottom: 12px; color: var(--text-4); display: block; margin: 0 auto 12px; }
.spinner {
  width: 22px; height: 22px; border: 2.5px solid var(--border); border-top-color: var(--primary);
  border-radius: 50%; animation: spin .6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ═══ Modal ═══ */
.modal-overlay {
  position: fixed; inset: 0; z-index: 999;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,.45); backdrop-filter: blur(4px);
}
.modal-box {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px; width: 620px; max-height: 82vh;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 25px 80px rgba(0,0,0,.2);
}
.modal-md { width: 520px; }
.modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.modal-head h3 { font-size: 16px; font-weight: 700; margin: 0; }
.modal-x {
  background: none; border: none; color: var(--text-3); font-size: 24px;
  cursor: pointer; line-height: 1; padding: 4px; border-radius: 6px;
  transition: all .15s;
}
.modal-x:hover { background: var(--bg); color: var(--text); }
.modal-content { overflow-y: auto; padding: 24px; }

.detail-grid { display: grid; grid-template-columns: 120px 1fr; gap: 12px 20px; font-size: 13.5px; }
.dg-row { display: contents; }
.dg-k { color: var(--text-3); text-align: right; font-weight: 500; }
.dg-v { color: var(--text); word-break: break-all; }
.dg-v strong { font-weight: 700; }
.dg-v.amount-lg { font-size: 20px; font-weight: 800; color: var(--success); }
.dg-full { grid-column: 1/-1; }
.dg-pre { font-size: 12px; background: var(--bg); padding: 12px; border-radius: 8px; margin: 0; white-space: pre-wrap; font-family: 'Cascadia Code','Consolas',monospace; max-height: 200px; overflow: auto; }

/* Editor form */
.ef-form .ef-field { margin-bottom: 14px; }
.ef-form label { display: block; font-size: 12.5px; color: var(--text-3); font-weight: 600; margin-bottom: 5px; letter-spacing: .02em; }
.ef-form input, .ef-form textarea, .ef-form select {
  width: 100%; padding: 10px 13px; border-radius: 8px; border: 1px solid var(--border);
  background: var(--bg); color: var(--text); font-size: 13.5px;
  outline: none; font-family: inherit; resize: vertical; transition: border-color .15s;
  box-sizing: border-box;
}
.ef-form input:focus, .ef-form textarea:focus, .ef-form select:focus { border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-soft); }
.ef-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.ef-check { margin: 8px 0 16px; }
.checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: var(--text-2); }
.checkbox-label input[type=checkbox] { accent-color: var(--primary); width: 16px; height: 16px; }
.ef-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }

/* Toast */
.toast {
  position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
  padding: 10px 24px; border-radius: 10px; font-size: 13.5px; font-weight: 600;
  z-index: 9999; letter-spacing: .01em;
  box-shadow: 0 8px 30px rgba(0,0,0,.15);
}
.toast.info { background: var(--surface); color: var(--text-2); border: 1px solid var(--border); }
.toast.success { background: var(--success); color: #fff; }
.toast.error { background: var(--danger); color: #fff; }
.toast.warn { background: var(--warning); color: #000; }

/* Transitions */
.modal-enter-active, .modal-leave-active { transition: opacity .2s ease; }
.modal-enter-active .modal-box, .modal-leave-active .modal-box { transition: transform .25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .modal-box, .modal-leave-to .modal-box { transform: scale(.95) translateY(10px); }

.toast-anim-enter-active { transition: all .25s ease; }
.toast-anim-leave-active { transition: all .2s ease; }
.toast-anim-enter-from { opacity: 0; transform: translateX(-50%) translateY(16px); }
.toast-anim-leave-to { opacity: 0; transform: translateX(-50%) translateY(-8px); }

/* Responsive */
@media (max-width: 1024px) {
  .stat-cards { grid-template-columns: repeat(2, 1fr); }
  .dash-grid { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .sidebar { width: 64px; padding: 0; }
  .sb-brand span, .sb-item span:not(.sb-count), .sb-count, .sb-bottom { display: none; }
  .sb-item { justify-content: center; padding: 12px 0; }
  .sb-brand { justify-content: center; padding: 16px 0; }
  .stat-cards { grid-template-columns: 1fr; }
  .toolbar { flex-direction: column; align-items: stretch; }
  .search-box { min-width: unset; }
  .modal-box { width: 94vw !important; }
  .detail-grid { grid-template-columns: 1fr; }
  .detail-grid .dg-k { text-align: left; margin-top: 8px; }
  .ef-row { grid-template-columns: 1fr; }
}
</style>
